import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH, PLAYER_RADIUS } from '../config/game';
import type { SeededRandom } from './seededRandom';
import { getTunables } from '../config/tunables';

export type SpecialDropKind = 'heal-orb' | 'xp-orb' | 'haste-orb' | 'material-crate' | 'upgrade-chest';

export interface SpecialDropPickupPayload {
  kind: SpecialDropKind;
  x: number;
  y: number;
  value: number;
}

type DropKind = 'material' | SpecialDropKind;

export type DropVisualSnapshot = {
  ref: Phaser.GameObjects.Rectangle;
  id: number;
  x: number;
  y: number;
  kind: DropKind;
  state: 'idle' | 'magnetic';
};

export class MaterialSystem {
  private materials: Phaser.GameObjects.Group;
  private pickupRadius: number;
  private pickupRadiusMultiplier: number;
  private dropCounter = 0;
  
  constructor(
    private readonly scene: Phaser.Scene,
    private readonly player: Phaser.GameObjects.Arc,
    private readonly rng: SeededRandom,
    pickupRadiusMultiplier = 1,
  ) {
    if (!Number.isFinite(pickupRadiusMultiplier) || pickupRadiusMultiplier <= 0) {
      throw new Error(`Invalid pickup radius multiplier: ${pickupRadiusMultiplier}`);
    }
    this.pickupRadiusMultiplier = pickupRadiusMultiplier;
    this.pickupRadius = PLAYER_RADIUS * getTunables().economy.pickupRadiusBaseMultiplier * pickupRadiusMultiplier;
    this.materials = this.scene.add.group();
    
    this.scene.events.on('enemy-died', (x: number, y: number) => {
      const configuredDrops = getTunables().economy.materialDropsPerEnemy;
      const guaranteedDrops = Math.max(1, Math.floor(configuredDrops));
      const extraDropChance = Math.max(0, configuredDrops - guaranteedDrops);
      const drops = guaranteedDrops + (this.rng.next() < extraDropChance ? 1 : 0);
      for (let index = 0; index < drops; index += 1) {
        this.dropMaterial(x, y);
      }
      this.maybeDropCombatReward(x, y);
    });

    this.scene.events.on('elite-died', (x: number, y: number) => {
      this.dropEliteReward(x, y);
    });

    this.scene.events.on('boss-material-eat-request', (payload: {
      x: number;
      y: number;
      seekRange: number;
      consumeRange: number;
      target?: { x: number; y: number };
      consumed?: boolean;
    }) => {
      this.resolveBossMaterialEat(payload);
    });

    // Material Pickup Collision
    this.scene.physics.add.overlap(this.player, this.materials, (_player, matObj) => {
      const mat = matObj as Phaser.GameObjects.Rectangle;
      if (mat.active) {
          this.collectMaterial(mat);
      }
    });
  }

  setPickupRadiusMultiplier(multiplier: number) {
    if (!Number.isFinite(multiplier) || multiplier <= 0) {
      throw new Error(`Invalid pickup radius multiplier: ${multiplier}`);
    }
    if (Math.abs(multiplier - this.pickupRadiusMultiplier) < 0.001) {
      return;
    }
    this.pickupRadiusMultiplier = multiplier;
    this.pickupRadius = PLAYER_RADIUS * getTunables().economy.pickupRadiusBaseMultiplier * multiplier;
  }

  dropMaterial(x: number, y: number) {
    this.dropToken('material', x, y);
  }

  dropUpgradeReward(x: number, y: number, kind: Extract<SpecialDropKind, 'heal-orb' | 'xp-orb' | 'haste-orb'>) {
    this.dropToken(kind, x, y);
  }

  dropEliteReward(x: number, y: number) {
    const roll = this.rng.next();
    const kind: SpecialDropKind = roll < 0.42 ? 'upgrade-chest' : roll < 0.68 ? 'material-crate' : roll < 0.84 ? 'xp-orb' : roll < 0.94 ? 'haste-orb' : 'heal-orb';
    this.dropToken(kind, x, y);
  }

  maybeDropTreasureChest(wave: number, playerX: number, playerY: number) {
    const specialDrops = getTunables().specialDrops;
    if (wave < specialDrops.treasureMinWave || this.rng.next() >= specialDrops.treasureChancePerWave) {
      return false;
    }

    const angle = this.rng.next() * Math.PI * 2;
    const distance = this.rng.integer(60, 132);
    const x = Phaser.Math.Clamp(playerX + Math.cos(angle) * distance, 34, GAME_WIDTH - 34);
    const y = Phaser.Math.Clamp(playerY + Math.sin(angle) * distance, 110, GAME_HEIGHT - 84);
    this.scene.time.delayedCall(specialDrops.treasureSpawnDelayMs, () => {
      this.dropToken('upgrade-chest', x, y);
    });
    return true;
  }

  private dropToken(kind: DropKind, x: number, y: number) {
    let mat = this.materials.getFirstDead(false) as Phaser.GameObjects.Rectangle;
    if (!mat) {
        if (this.materials.getLength() < 100) {
            mat = this.scene.add.rectangle(x, y, 8, 8, 0x88ff88);
            this.scene.physics.add.existing(mat);
            this.materials.add(mat);
        } else {
            return;
        }
    }

    const visual = this.getDropVisual(kind);
    this.scene.tweens.killTweensOf(mat);
    mat.setPosition(x, y);
    mat.setSize(visual.width, visual.height);
    mat.setFillStyle(visual.color, visual.alpha);
    mat.setStrokeStyle(visual.strokeWidth, visual.strokeColor, visual.strokeAlpha);
    mat.setActive(true).setVisible(true);
    mat.setDepth(kind === 'material' ? 15 : 34);
    mat.setData('dropKind', kind);
    mat.setData('state', 'idle');
    const dropId = this.dropCounter + 1;
    this.dropCounter = dropId;
    mat.setData('dropId', dropId);
    
    const body = mat.body as Phaser.Physics.Arcade.Body;
    body.enable = true;
    body.setSize(visual.width, visual.height, true);
    
    const angle = this.rng.next() * Math.PI * 2;
    const speed = kind === 'upgrade-chest' ? 0 : this.rng.next() * 80;
    body.setVelocity(Math.cos(angle) * speed, Math.sin(angle) * speed);
    body.setDamping(false);
    body.setDrag(520, 520);

    mat.setScale(1);
    if (kind !== 'material') {
      this.scene.tweens.add({
        targets: mat,
        scale: 1.12,
        duration: 420,
        yoyo: true,
        repeat: -1,
      });
    } else {
      mat.setScale(1);
    }

    this.scene.time.delayedCall(220, () => {
      if (!mat.active || mat.getData('state') !== 'idle' || mat.getData('dropId') !== dropId) {
        return;
      }

      body.setVelocity(0, 0);
      body.setDrag(0, 0);
    });
  }

  private maybeDropCombatReward(x: number, y: number) {
    const rewardChance = Math.min(0.35, getTunables().specialDrops.upgradeDropChance * 0.18);
    if (this.rng.next() >= rewardChance) {
      return;
    }

    const roll = this.rng.next();
    const kind: SpecialDropKind = roll < 0.34
      ? 'xp-orb'
      : roll < 0.56
        ? 'material-crate'
        : roll < 0.76
          ? 'upgrade-chest'
          : roll < 0.9
            ? 'haste-orb'
            : 'heal-orb';
    this.dropToken(kind, x, y);
  }

  update() {
    this.materials.getChildren().forEach((child) => {
      const mat = child as Phaser.GameObjects.Rectangle;
      if (!mat.active) return;
      
      const state = mat.getData('state');
      const distSq = Phaser.Math.Distance.Squared(this.player.x, this.player.y, mat.x, mat.y);
      const radiusSq = this.pickupRadius * this.pickupRadius;
      
      if (state === 'idle' && distSq < radiusSq) {
        mat.setData('state', 'magnetic');
        (mat.body as Phaser.Physics.Arcade.Body).setDamping(false);
        (mat.body as Phaser.Physics.Arcade.Body).setDrag(0, 0);
      }
      
      if (mat.getData('state') === 'magnetic') {
        this.scene.physics.moveToObject(mat, this.player, 520);
      } else {
        const body = mat.body as Phaser.Physics.Arcade.Body;
        if (body.speed < 8) {
          body.setVelocity(0, 0);
          body.setDrag(0, 0);
        }
      }
    });
  }

  getActiveDrops() {
    return this.materials.getChildren()
      .map((child) => child as Phaser.GameObjects.Rectangle)
      .filter((mat) => mat.active)
      .map((mat) => ({
        x: mat.x,
        y: mat.y,
        kind: (mat.getData('dropKind') as DropKind | undefined) ?? 'material',
      }));
  }

  getDropVisuals(): DropVisualSnapshot[] {
    return this.materials.getChildren()
      .map((child) => child as Phaser.GameObjects.Rectangle)
      .filter((mat) => mat.active)
      .map((mat) => ({
        ref: mat,
        id: (mat.getData('dropId') as number | undefined) ?? 0,
        x: mat.x,
        y: mat.y,
        kind: (mat.getData('dropKind') as DropKind | undefined) ?? 'material',
        state: ((mat.getData('state') as DropVisualSnapshot['state'] | undefined) ?? 'idle'),
      }));
  }

  clearAllAndCollect() {
    this.materials.getChildren().forEach((child) => {
      const mat = child as Phaser.GameObjects.Rectangle;
      if (mat.active) {
        this.collectMaterial(mat);
      }
    });
  }

  private resolveBossMaterialEat(payload: {
    x: number;
    y: number;
    seekRange: number;
    consumeRange: number;
    target?: { x: number; y: number };
    consumed?: boolean;
  }) {
    if (
      !Number.isFinite(payload.x) ||
      !Number.isFinite(payload.y) ||
      !Number.isFinite(payload.seekRange) ||
      !Number.isFinite(payload.consumeRange) ||
      payload.seekRange <= 0 ||
      payload.consumeRange <= 0
    ) {
      throw new Error('Invalid boss material eat request');
    }

    let nearest: Phaser.GameObjects.Rectangle | undefined;
    let nearestDistSq = payload.seekRange * payload.seekRange;
    this.materials.getChildren().forEach((child) => {
      const mat = child as Phaser.GameObjects.Rectangle;
      if (!mat.active) {
        return;
      }
      if (((mat.getData('dropKind') as DropKind | undefined) ?? 'material') !== 'material') {
        return;
      }
      const distSq = Phaser.Math.Distance.Squared(payload.x, payload.y, mat.x, mat.y);
      if (distSq <= nearestDistSq) {
        nearest = mat;
        nearestDistSq = distSq;
      }
    });

    if (!nearest) {
      payload.consumed = false;
      return;
    }

    payload.target = { x: nearest.x, y: nearest.y };
    if (nearestDistSq <= payload.consumeRange * payload.consumeRange) {
      this.consumeByBoss(nearest);
      payload.consumed = true;
      return;
    }

    payload.consumed = false;
  }

  private consumeByBoss(mat: Phaser.GameObjects.Rectangle) {
    this.scene.tweens.killTweensOf(mat);
    mat.setActive(false).setVisible(false);
    const body = mat.body as Phaser.Physics.Arcade.Body;
    body.setVelocity(0, 0);
    body.setDrag(0, 0);
    body.enable = false;
  }

  private collectMaterial(mat: Phaser.GameObjects.Rectangle) {
    const kind = (mat.getData('dropKind') as DropKind | undefined) ?? 'material';
    const payload = this.getSpecialDropPayload(kind, mat.x, mat.y);
    this.scene.events.emit('material-visual-picked', { x: mat.x, y: mat.y, kind });
    this.scene.tweens.killTweensOf(mat);
    mat.setActive(false).setVisible(false);
    const body = mat.body as Phaser.Physics.Arcade.Body;
    body.setVelocity(0, 0);
    body.setDrag(0, 0);
    body.enable = false;
    if (payload) {
      this.scene.events.emit('special-drop-picked', payload);
      return;
    }
    this.scene.events.emit('material-picked', getTunables().economy.materialValue);
  }

  private getSpecialDropPayload(kind: DropKind, x: number, y: number): SpecialDropPickupPayload | null {
    const specialDrops = getTunables().specialDrops;
    if (kind === 'heal-orb') {
      return { kind, x, y, value: specialDrops.healAmount };
    }
    if (kind === 'xp-orb') {
      return { kind, x, y, value: specialDrops.xpAmount };
    }
    if (kind === 'haste-orb') {
      return { kind, x, y, value: specialDrops.hasteMoveSpeedBonus };
    }
    if (kind === 'material-crate') {
      return { kind, x, y, value: specialDrops.eliteMaterialReward };
    }
    if (kind === 'upgrade-chest') {
      return { kind, x, y, value: 1 };
    }
    return null;
  }

  private getDropVisual(kind: DropKind) {
    if (kind === 'heal-orb') {
      return { width: 14, height: 14, color: 0x62ff93, alpha: 1, strokeColor: 0xd8ffe3, strokeAlpha: 0.95, strokeWidth: 2 };
    }
    if (kind === 'xp-orb') {
      return { width: 14, height: 14, color: 0xffdf62, alpha: 1, strokeColor: 0xfff2b0, strokeAlpha: 0.95, strokeWidth: 2 };
    }
    if (kind === 'haste-orb') {
      return { width: 14, height: 14, color: 0x64caff, alpha: 1, strokeColor: 0xd4f5ff, strokeAlpha: 0.95, strokeWidth: 2 };
    }
    if (kind === 'material-crate') {
      return { width: 18, height: 18, color: 0xf0b84f, alpha: 1, strokeColor: 0xfff0bd, strokeAlpha: 0.95, strokeWidth: 2 };
    }
    if (kind === 'upgrade-chest') {
      return { width: 22, height: 18, color: 0xd488ff, alpha: 1, strokeColor: 0xf3d8ff, strokeAlpha: 0.95, strokeWidth: 2 };
    }
    return { width: 8, height: 8, color: 0x88ff88, alpha: 1, strokeColor: 0x1c5a30, strokeAlpha: 0, strokeWidth: 0 };
  }
}
